import { type FormEvent, useMemo, useState } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import {
    AppDataTable
} from "@/components/ui/app-data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
    Search,
    Plus,
    Filter,
    ExternalLink,
    ShieldCheck,
    ShieldAlert,
    ShieldEllipsis
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface RiskItem {
    id: string;
    name: string;
    category: string;
    owner: string;
    likelihood: number;
    impact: number;
    score: number;
    status: string;
    policy: string;
}

const RISK_CATEGORIES = ["Technology", "Compliance", "Operational", "Strategic", "Reputational"] as const;
const RISK_OWNERS = ["Sarah Chen", "James Wilson", "Robert Taylor", "Linda Moore", "Michael Brown"] as const;
const RISK_STATUS = ["Open", "Mitigating", "Closed"] as const;

const INITIAL_RISKS: RiskItem[] = [
    { id: "RSK-001", name: "Data Breach in Production", category: "Technology", owner: "Sarah Chen", likelihood: 2, impact: 5, score: 10, status: "Mitigating", policy: "Data Protection Policy" },
    { id: "RSK-002", name: "Non-compliance with GDPR", category: "Compliance", owner: "James Wilson", likelihood: 1, impact: 5, score: 5, status: "Open", policy: "Privacy Policy" },
    { id: "RSK-003", name: "Third-party Vendor Failure", category: "Operational", owner: "Robert Taylor", likelihood: 3, impact: 4, score: 12, status: "Mitigating", policy: "Vendor Management Policy" },
    { id: "RSK-004", name: "Market Volatility", category: "Strategic", owner: "Linda Moore", likelihood: 4, impact: 3, score: 12, status: "Open", policy: "Investment Policy" },
    { id: "RSK-005", name: "Internal Fraud Potential", category: "Operational", owner: "Michael Brown", likelihood: 2, impact: 4, score: 8, status: "Mitigating", policy: "Internal Controls Policy" },
    { id: "RSK-006", name: "Ransomware Attack", category: "Technology", owner: "Sarah Chen", likelihood: 3, impact: 5, score: 15, status: "Open", policy: "Cybersecurity Policy" },
    { id: "RSK-007", name: "Public Relations Crisis", category: "Reputational", owner: "Linda Moore", likelihood: 2, impact: 3, score: 6, status: "Closed", policy: "Communication Policy" },
    { id: "RSK-008", name: "Regulatory Change Surprise", category: "Compliance", owner: "James Wilson", likelihood: 2, impact: 4, score: 8, status: "Open", policy: "None" },
    { id: "RSK-009", name: "Business Continuity Failure", category: "Operational", owner: "Robert Taylor", likelihood: 1, impact: 5, score: 5, status: "Mitigating", policy: "Business Continuity Plan" },
    { id: "RSK-010", name: "API Rate Limiting Issues", category: "Technology", owner: "Sarah Chen", likelihood: 4, impact: 2, score: 8, status: "Open", policy: "None" },
];

type RiskFormState = {
    name: string;
    category: string;
    owner: string;
    likelihood: string;
    impact: string;
    status: string;
    policy: string;
};

const DEFAULT_FORM: RiskFormState = {
    name: "",
    category: "",
    owner: "",
    likelihood: "1",
    impact: "1",
    status: "Open",
    policy: ""
};

const getScoreColor = (score: number) => {
    if (score >= 12) return "bg-red-500/10 text-red-600 dark:text-red-400 border-red-200 dark:border-red-900/30";
    if (score >= 8) return "bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-200 dark:border-orange-900/30";
    return "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-900/30";
};

const getStatusIcon = (status: string) => {
    switch (status) {
        case "Open": return <ShieldAlert className="h-4 w-4 mr-1 text-red-500 animate-pulse" />;
        case "Mitigating": return <ShieldEllipsis className="h-4 w-4 mr-1 text-orange-500" />;
        case "Closed": return <ShieldCheck className="h-4 w-4 mr-1 text-emerald-500" />;
        default: return null;
    }
};

export default function RiskRegister() {
    const { toast } = useToast();
    const [risks, setRisks] = useState<RiskItem[]>(INITIAL_RISKS);
    const [searchTerm, setSearchTerm] = useState("");
    const [isRecordRiskOpen, setIsRecordRiskOpen] = useState(false);
    const [form, setForm] = useState<RiskFormState>(DEFAULT_FORM);

    const filteredRisks = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) {
            return risks;
        }
        return risks.filter((risk) => {
            return (
                risk.id.toLowerCase().includes(term) ||
                risk.name.toLowerCase().includes(term) ||
                risk.owner.toLowerCase().includes(term) ||
                risk.category.toLowerCase().includes(term)
            );
        });
    }, [risks, searchTerm]);

    const nextRiskId = useMemo(() => {
        const maxIndex = risks.reduce((max, risk) => {
            const numeric = Number.parseInt(risk.id.replace("RSK-", ""), 10);
            return Number.isNaN(numeric) ? max : Math.max(max, numeric);
        }, 0);
        return `RSK-${String(maxIndex + 1).padStart(3, "0")}`;
    }, [risks]);

    const previewScore = Number(form.likelihood) * Number(form.impact);

    const isFormValid = Boolean(
        form.name.trim() &&
        form.category &&
        form.owner &&
        form.status &&
        form.policy.trim()
    );

    const resetForm = () => {
        setForm(DEFAULT_FORM);
    };

    const handleCreateRisk = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!isFormValid) {
            return;
        }

        const likelihood = Number(form.likelihood);
        const impact = Number(form.impact);
        const newRisk: RiskItem = {
            id: nextRiskId,
            name: form.name.trim(),
            category: form.category,
            owner: form.owner,
            likelihood,
            impact,
            score: likelihood * impact,
            status: form.status,
            policy: form.policy.trim()
        };

        setRisks((current) => [newRisk, ...current]);
        setIsRecordRiskOpen(false);
        resetForm();
        toast({
            title: "Risk recorded",
            description: `${newRisk.id} has been added to the central register.`
        });
    };

    const columns = useMemo<ColumnDef<RiskItem>[]>(() => [
        {
            accessorKey: "id",
            header: "ID",
            cell: ({ row }) => <span className="font-mono text-xs text-muted-foreground">{row.original.id}</span>,
            meta: { headerClassName: "w-[100px]" }
        },
        {
            accessorKey: "name",
            header: "Risk Name",
            cell: ({ row }) => <span className="font-medium">{row.original.name}</span>
        },
        {
            accessorKey: "category",
            header: "Category",
            cell: ({ row }) => (
                <Badge variant="outline" className="font-normal border-muted-foreground/20">
                    {row.original.category}
                </Badge>
            )
        },
        {
            accessorKey: "owner",
            header: "Owner",
            cell: ({ row }) => <span className="text-sm">{row.original.owner}</span>
        },
        {
            accessorKey: "score",
            header: "Score",
            cell: ({ row }) => (
                <div className="text-center">
                    <div className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold border ${getScoreColor(row.original.score)}`}>
                        {row.original.score}
                    </div>
                </div>
            ),
            meta: { headerClassName: "text-center" }
        },
        {
            accessorKey: "status",
            header: "Status",
            cell: ({ row }) => (
                <div className="flex items-center text-sm">
                    {getStatusIcon(row.original.status)}
                    {row.original.status}
                </div>
            )
        },
        {
            accessorKey: "policy",
            header: "Linked Policy",
            cell: ({ row }) => (
                <div className="flex items-center gap-1.5 transition-colors group-hover:text-primary">
                    {row.original.policy === "None" ? (
                        <span className="text-sm text-amber-500/80 font-medium italic underline decoration-dotted">No policy linked</span>
                    ) : (
                        <>
                            <span className="text-sm truncate max-w-[150px]">{row.original.policy}</span>
                            <ExternalLink className="h-3 w-3 opacity-50" />
                        </>
                    )}
                </div>
            )
        }
    ], []);

    return (
        <div className="space-y-6 max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-medium text-foreground">Risk Register</h1>
                    <p className="text-muted-foreground">Comprehensive record of all identified organizational risks.</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" className="shadow-premium">
                        <Filter className="h-4 w-4 mr-2" />
                        Filters
                    </Button>
                    <Button
                        className="bg-primary hover:bg-primary/90 shadow-premium"
                        onClick={() => setIsRecordRiskOpen(true)}
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        Record Risk
                    </Button>
                </div>
            </div>

            <Tabs defaultValue="register" className="w-full">
                <TabsList className="bg-muted/50 p-1">
                    <TabsTrigger value="register">Central Register</TabsTrigger>
                    <TabsTrigger value="traceability">Policy Traceability</TabsTrigger>
                </TabsList>

                <TabsContent value="register" className="space-y-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search risks by name, owner, or ID..."
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            className="pl-10 h-11 border-none bg-muted/30 focus-visible:ring-primary/20"
                        />
                    </div>

                    <div className="border rounded-xl bg-card/50 overflow-hidden shadow-premium">
                        <AppDataTable
                            columns={columns}
                            data={filteredRisks}
                            headerClassName="bg-muted/30"
                            rowClassName={() => "group hover:bg-primary/5 transition-colors"}
                            emptyMessage="No risks found."
                        />
                    </div>
                </TabsContent>

                <TabsContent value="traceability">
                    <Card className="border-none shadow-premium bg-gradient-to-br from-card to-card/50">
                        <CardHeader>
                            <CardTitle>Risk-to-Policy Mapping</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid gap-4">
                                {risks.filter(r => r.policy !== "None").map((risk) => (
                                    <div key={risk.id} className="flex items-center justify-between p-4 rounded-lg bg-muted/40 border border-transparent hover:border-primary/20 transition-all">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-sm font-semibold">{risk.name}</span>
                                            <span className="text-xs text-muted-foreground">Managed via: <span className="text-primary font-medium">{risk.policy}</span></span>
                                        </div>
                                        <Badge className="bg-primary/10 text-primary hover:bg-primary/20 border-transparent">
                                            Compliant
                                        </Badge>
                                    </div>
                                ))}
                                {risks.filter(r => r.policy === "None").map((risk) => (
                                    <div key={risk.id} className="flex items-center justify-between p-4 rounded-lg bg-amber-500/5 border border-amber-500/20">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-sm font-semibold">{risk.name}</span>
                                            <span className="text-xs text-amber-600/80 font-medium italic">Gap: No control policy currently linked to this risk.</span>
                                        </div>
                                        <Button size="sm" variant="ghost" className="text-xs text-amber-600 hover:bg-amber-500/10">
                                            Link Control
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            <Dialog open={isRecordRiskOpen} onOpenChange={(open) => {
                setIsRecordRiskOpen(open);
                if (!open) {
                    resetForm();
                }
            }}>
                <DialogContent className="max-h-[90vh] max-w-2xl">
                    <form className="flex max-h-[calc(90vh-3rem)] flex-col gap-6" onSubmit={handleCreateRisk}>
                        <DialogHeader>
                            <DialogTitle>Record Risk</DialogTitle>
                            <DialogDescription>
                                Add a new risk to the central register and policy traceability map.
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-5 overflow-y-auto pr-1">
                            <div className="space-y-2">
                                <Label htmlFor="risk-id">Risk ID</Label>
                                <Input id="risk-id" value={nextRiskId} disabled />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="risk-name">Risk Name</Label>
                                <Input
                                    id="risk-name"
                                    value={form.name}
                                    onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                                    placeholder="e.g., Critical supplier outage"
                                    required
                                />
                            </div>

                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div className="space-y-2">
                                    <Label>Category</Label>
                                    <Select value={form.category} onValueChange={(value) => setForm((current) => ({ ...current, category: value }))}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select category" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {RISK_CATEGORIES.map((category) => (
                                                <SelectItem key={category} value={category}>
                                                    {category}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label>Owner</Label>
                                    <Select value={form.owner} onValueChange={(value) => setForm((current) => ({ ...current, owner: value }))}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select owner" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {RISK_OWNERS.map((owner) => (
                                                <SelectItem key={owner} value={owner}>
                                                    {owner}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div className="space-y-2">
                                    <Label>Likelihood (1-5)</Label>
                                    <Select value={form.likelihood} onValueChange={(value) => setForm((current) => ({ ...current, likelihood: value }))}>
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {["1", "2", "3", "4", "5"].map((value) => (
                                                <SelectItem key={value} value={value}>
                                                    {value}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label>Impact (1-5)</Label>
                                    <Select value={form.impact} onValueChange={(value) => setForm((current) => ({ ...current, impact: value }))}>
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {["1", "2", "3", "4", "5"].map((value) => (
                                                <SelectItem key={value} value={value}>
                                                    {value}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Status</Label>
                                <Select value={form.status} onValueChange={(value) => setForm((current) => ({ ...current, status: value }))}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {RISK_STATUS.map((status) => (
                                            <SelectItem key={status} value={status}>
                                                {status}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="linked-policy">Linked Policy</Label>
                                <Input
                                    id="linked-policy"
                                    value={form.policy}
                                    onChange={(event) => setForm((current) => ({ ...current, policy: event.target.value }))}
                                    placeholder="e.g., Information Security Policy"
                                    required
                                />
                            </div>

                            <div className="rounded-lg border bg-muted/30 p-4">
                                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Calculated Score</p>
                                <p className="mt-2 text-2xl font-bold">{previewScore}</p>
                                <p className="text-xs text-muted-foreground">
                                    Score is automatically calculated as likelihood x impact.
                                </p>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="risk-notes">Notes</Label>
                                <Textarea
                                    id="risk-notes"
                                    placeholder="Optional: context, mitigation actions, or next review notes."
                                />
                            </div>
                        </div>

                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setIsRecordRiskOpen(false);
                                    resetForm();
                                }}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" disabled={!isFormValid}>
                                Save Risk
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
